import React, { useState, useEffect, ReactNode } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Card,
  CardContent,
  Badge,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
} from "@semo/shared-ui";
import {
  Image,
  Loader2,
  X,
  Ruler,
  DollarSign,
  FileText,
  Check,
  ChevronsUpDown,
  Layers,
  MessageSquare,
} from "@semo/icons";
import {
  AdminCategoryItemDto,
  CategoryTreeNodeDto,
  CreateAdminProductDto,
  SupplierItemRequestSummaryDto,
  ApproveSupplierItemRequestDto,
} from "@semo/api-contracts";
import {
  flattenCategoryTree,
  type FlattenedCategoryResult,
} from "../utils/categories";
import {
  uploadProductImage,
  validateProductImageUrl,
  PRODUCT_IMAGE_EXTENSIONS,
  PRODUCT_IMAGE_MAX_BYTES,
} from "../utils/product-image";

/**
 * Form data structure combining fields needed for product creation and request approval.
 */
export type ProductFormData = CreateAdminProductDto &
  Pick<ApproveSupplierItemRequestDto, "adminComment"> & {
    requestReason?: string;
  };

export interface ProductFormSlots {
  header?: ReactNode;
  beforeBasicInfo?: ReactNode;
  afterBasicInfo?: ReactNode;
  beforeSpecs?: ReactNode;
  afterSpecs?: ReactNode;
  beforeRequestReason?: ReactNode;
  afterRequestReason?: ReactNode;
  beforeAdminComment?: ReactNode;
  afterAdminComment?: ReactNode;
  footer?: ReactNode;
}

export interface ProductFormSections {
  category?: boolean;
  basicInfo?: boolean;
  specs?: boolean;
  pricing?: boolean;
  image?: boolean;
  requestReason?: boolean;
  adminComment?: boolean;
}

export interface ProductFormProps {
  categoryId: string;
  categoryName: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ProductFormData) => void | Promise<void>;
  categories?: CategoryTreeNodeDto[];
  title?: string;
  submitLabel?: string;
  initialData?: Partial<AdminCategoryItemDto | SupplierItemRequestSummaryDto>;
  onReject?: () => void;
  rejectLabel?: string;
  isSubmitting?: boolean;
  submitError?: Error | null;
  slots?: ProductFormSlots;
  sections?: ProductFormSections;
  children?: ReactNode;
}

const ProductFormComponent: React.FC<ProductFormProps> = ({
  categoryId,
  categoryName,
  isOpen,
  onClose,
  onSubmit,
  categories: categoriesTree,
  title = "상품 등록",
  submitLabel = "상품 등록 완료",
  initialData,
  onReject,
  rejectLabel = "반려",
  isSubmitting = false,
  submitError = null,
  slots,
  sections = {},
  children,
}) => {
  const [openCategory, setOpenCategory] = useState(false);
  const [imageTab, setImageTab] = useState<"upload" | "url">("upload");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  const categoriesInfo = React.useMemo<{
    list: CategoryTreeNodeDto[];
    pathMap: Map<string, string>;
  }>(() => {
    if (!categoriesTree)
      return {
        list: [],
        pathMap: new Map<string, string>(),
      };
    const flattened = flattenCategoryTree(categoriesTree);
    const list = flattened.map(
      (item: FlattenedCategoryResult<CategoryTreeNodeDto>) => item.original,
    );
    const pathMap = new Map<string, string>();
    flattened.forEach((item: FlattenedCategoryResult<CategoryTreeNodeDto>) => {
      pathMap.set(item.original.id, item.fullPath);
    });
    return { list, pathMap };
  }, [categoriesTree]);

  const categories = categoriesInfo.list;
  const showCategory = sections.category !== false;
  const showBasicInfo = sections.basicInfo !== false;
  const showSpecs = sections.specs !== false;
  const showPricing = sections.pricing !== false;
  const showImage = sections.image !== false;
  const showRequestReason = sections.requestReason === true;
  const showAdminComment = sections.adminComment === true;

  const getFullCategoryName = (id: string) =>
    categoriesInfo.pathMap.get(id) ?? "";

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ProductFormData>({
    defaultValues: {
      categoryId,
      name: "",
      spec1: "",
      spec2: "",
      spec3: "",
      brand: "",
      manufacturer: "",
      unit: "EA",
      price: 0,
      imageUrl: "",
      requestReason: "",
      adminComment: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // Safe mapping from Union type (AdminCategoryItemDto | SupplierItemRequestSummaryDto)
        const dataWithReason: ProductFormData = {
          categoryId: initialData.categoryId ?? categoryId,
          name: initialData.name ?? "",
          spec1: initialData.spec1 ?? "",
          spec2: initialData.spec2 ?? "",
          spec3: initialData.spec3 ?? "",
          brand: initialData.brand ?? "",
          manufacturer: initialData.manufacturer ?? "",
          unit: initialData.unit ?? "EA",
          price: "price" in initialData ? (initialData.price ?? 0) : 0,
          imageUrl:
            "imageUrl" in initialData ? (initialData.imageUrl ?? "") : "",
          requestReason:
            "requestReason" in initialData
              ? (initialData.requestReason ?? "")
              : "",
          adminComment:
            "adminComment" in initialData
              ? (initialData.adminComment ?? "")
              : "",
        };
        reset(dataWithReason);
        if (dataWithReason.imageUrl) setImageTab("url");
      } else {
        reset({
          categoryId,
          name: "",
          spec1: "",
          spec2: "",
          spec3: "",
          brand: "",
          manufacturer: "",
          unit: "EA",
          price: 0,
          imageUrl: "",
          requestReason: "",
          adminComment: "",
        });
      }
    }
  }, [isOpen, categoryId, initialData, reset]);

  const handleFormSubmit = async (data: ProductFormData) => {
    // 죽은 주소가 DB 로 들어가는 마지막 관문. blob:/data: 는 저장되면 복구가 안 된다.
    if (showImage && data.imageUrl) {
      const urlError = validateProductImageUrl(data.imageUrl);
      if (urlError) {
        setImageError(urlError);
        return;
      }
    }

    try {
      await onSubmit(data);
      reset();
      onClose();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // 같은 파일을 다시 고르는 경우에도 onChange 가 뜨도록 input 을 비운다.
    e.target.value = "";
    if (!file) return;

    setImageError(null);
    setIsUploadingImage(true);
    try {
      const { imageUrl: uploadedUrl } = await uploadProductImage(file);
      setValue("imageUrl", uploadedUrl, { shouldDirty: true });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "이미지 업로드에 실패했습니다";
      setImageError(message);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const imageUrl = watch("imageUrl");
  const clearImage = () => {
    setValue("imageUrl", "", { shouldDirty: true });
    setImageError(null);
  };

  // URL 탭에 직접 붙여넣은 주소 검증. 저장 전에 즉시 알려준다.
  const handleImageUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setImageError(validateProductImageUrl(e.target.value));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto p-0 gap-0 bg-muted">
        <DialogHeader className="p-6 pb-4 bg-background border-b sticky top-0 z-10">
          {slots?.header ?? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <DialogTitle className="text-xl">{title}</DialogTitle>
                <Badge
                  variant="default"
                  className="font-normal !bg-primary/15 !text-primary-text hover:!bg-primary/20 border-transparent shadow-none"
                >
                  {getFullCategoryName(watch("categoryId")) || categoryName}
                </Badge>
              </div>
              <DialogDescription>
                필수 정보를 입력하여 상품을 등록하거나 수정합니다.
              </DialogDescription>
            </>
          )}
        </DialogHeader>
        <form
          onSubmit={(e) => {
            void handleSubmit(handleFormSubmit)(e);
          }}
          className="p-6 space-y-8"
        >
          {submitError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-950/30 dark:border-red-900 flex gap-3">
              <div className="flex-shrink-0 text-red-600 dark:text-red-400">
                <svg
                  className="w-5 h-5 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-red-800 dark:text-red-200">
                  오류 발생
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {submitError.message}
                </p>
              </div>
            </div>
          )}
          {children}
          {showCategory && (
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-lg font-semibold text-foreground/80">
                <div className="p-1.5 bg-primary/10 text-primary-text dark:bg-primary/20 dark:text-primary-text rounded-md">
                  <Layers className="w-5 h-5" />
                </div>
                <h3>카테고리 설정</h3>
              </div>
              <Card className="border-none shadow-sm">
                <CardContent className="p-6 space-y-6">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      대표 카테고리 <span className="text-red-500">*</span>
                    </Label>
                    <Popover open={openCategory} onOpenChange={setOpenCategory}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openCategory}
                          className={cn(
                            "w-full justify-between",
                            !watch("categoryId") && "text-muted-foreground",
                          )}
                        >
                          {watch("categoryId")
                            ? getFullCategoryName(watch("categoryId"))
                            : "카테고리 검색 및 선택"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command className="overflow-visible">
                          <CommandInput placeholder="카테고리 검색..." />
                          <CommandList
                            className="max-h-[300px] overflow-y-auto pointer-events-auto"
                            onWheel={(e) => {
                              e.stopPropagation();
                            }}
                            onTouchMove={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <CommandEmpty>검색 결과가 없습니다.</CommandEmpty>
                            <CommandGroup
                              heading="카테고리 목록"
                              className="overflow-visible"
                            >
                              {categories.map((cat: CategoryTreeNodeDto) => (
                                <CommandItem
                                  key={cat.id}
                                  value={getFullCategoryName(cat.id)}
                                  onSelect={() => {
                                    setValue("categoryId", cat.id);
                                    setOpenCategory(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4 shrink-0",
                                      watch("categoryId") === cat.id
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  <div
                                    className="flex items-center gap-1 truncate"
                                    style={{
                                      paddingLeft: `${String((cat.level - 1) * 1)}rem`,
                                    }}
                                  >
                                    {cat.level > 1 && (
                                      <span className="text-muted-foreground/40 mr-1 select-none">
                                        └
                                      </span>
                                    )}
                                    <span>{cat.name}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">
                      * 상품 목록에 표시될 주 카테고리입니다.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </section>
          )}
          {slots?.beforeBasicInfo}
          {showBasicInfo && (
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-lg font-semibold text-foreground/80">
                <div className="p-1.5 bg-primary/10 text-primary-text dark:bg-primary/20 dark:text-primary-text rounded-md">
                  <FileText className="w-5 h-5" />
                </div>
                <h3>기본 정보</h3>
              </div>
              <Card className="border-none shadow-sm">
                <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 md:col-span-2">
                    <Label
                      htmlFor="name"
                      className="flex items-center gap-1 text-base"
                    >
                      물품명 <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      {...register("name", {
                        required: "물품명을 입력해주세요",
                      })}
                      placeholder="예: 테팔 매직핸즈 후라이팬 5종 세트"
                      className="h-11 text-base"
                    />
                    {errors.name && (
                      <p className="text-xs text-red-500">
                        {errors.name.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="manufacturer">제조사</Label>
                    <Input
                      id="manufacturer"
                      {...register("manufacturer")}
                      placeholder="예: 유한킴벌리"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="brand">브랜드</Label>
                    <Input
                      id="brand"
                      {...register("brand")}
                      placeholder="예: 크리넥스"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit">단위</Label>
                    <div className="flex gap-2">
                      <Input
                        id="unit"
                        {...register("unit")}
                        placeholder="직접 입력"
                        className="flex-1"
                      />
                      <Select
                        onValueChange={(v) => {
                          setValue("unit", v);
                        }}
                      >
                        <SelectTrigger className="w-[100px]">
                          <SelectValue placeholder="선택" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EA">EA (개)</SelectItem>
                          <SelectItem value="BOX">BOX (박스)</SelectItem>
                          <SelectItem value="SET">SET (세트)</SelectItem>
                          <SelectItem value="KG">KG (kg)</SelectItem>
                          <SelectItem value="M">M (m)</SelectItem>
                          <SelectItem value="ROLL">ROLL (롤)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          )}
          {slots?.afterBasicInfo}
          {slots?.beforeSpecs}
          {showSpecs && (
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-lg font-semibold text-foreground/80">
                <div className="p-1.5 bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 rounded-md">
                  <Ruler className="w-5 h-5" />
                </div>
                <h3>상세 규격</h3>
              </div>
              <Card className="border-none shadow-sm">
                <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="spec1" className="text-muted-foreground">
                      규격 (Spec 1)
                    </Label>
                    <Input
                      id="spec1"
                      {...register("spec1")}
                      placeholder="사이즈, 용량 등 (예: 28cm)"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="spec2" className="text-muted-foreground">
                      재질/타입 (Spec 2)
                    </Label>
                    <Input
                      id="spec2"
                      {...register("spec2")}
                      placeholder="재질, 색상 등 (예: 스테인리스)"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="spec3" className="text-muted-foreground">
                      패키징 (Spec 3)
                    </Label>
                    <Input
                      id="spec3"
                      {...register("spec3")}
                      placeholder="포장 단위 등 (예: 개별박스)"
                    />
                  </div>
                </CardContent>
              </Card>
            </section>
          )}
          {slots?.afterSpecs}
          {showRequestReason && (
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-lg font-semibold text-foreground/80">
                <div className="p-1.5 bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-md">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <h3>요청 사유</h3>
              </div>
              <Card className="border-none shadow-sm">
                <CardContent className="p-6">
                  <div className="space-y-2">
                    <Label htmlFor="requestReason">등록 요청 사유</Label>
                    <Textarea
                      id="requestReason"
                      {...register("requestReason")}
                      placeholder="이 물품의 등록이 필요한 이유를 설명해주세요."
                      className="min-h-[100px] resize-none"
                    />
                  </div>
                </CardContent>
              </Card>
            </section>
          )}
          {slots?.beforeRequestReason}
          {slots?.afterRequestReason}
          {showAdminComment && (
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-lg font-semibold text-foreground/80">
                <div className="p-1.5 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-md">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <h3>관리자 코멘트</h3>
              </div>
              <Card className="border-none shadow-sm">
                <CardContent className="p-6">
                  <div className="space-y-2">
                    <Label htmlFor="adminComment">
                      처리 메모 (공급자에게 노출됨)
                    </Label>
                    <Textarea
                      id="adminComment"
                      {...register("adminComment")}
                      placeholder="승인 또는 반려 사유를 입력해주세요. 이 내용은 공급자에게 전달됩니다."
                      className="min-h-[100px] resize-none"
                    />
                  </div>
                </CardContent>
              </Card>
            </section>
          )}
          {slots?.beforeAdminComment}
          {slots?.afterAdminComment}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {showPricing && (
              <section className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold text-foreground/80">
                  <div className="p-1.5 bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400 rounded-md">
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <h3>가격 정보</h3>
                </div>
                <Card className="border-none shadow-sm h-full">
                  <CardContent className="p-6 space-y-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="price"
                        className="flex items-center gap-1"
                      >
                        소비자 단가 <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-3 text-muted-foreground font-semibold">
                          ₩
                        </span>
                        <Input
                          id="price"
                          type="number"
                          className="pl-8 h-12 text-lg font-medium"
                          {...register("price", {
                            valueAsNumber: true,
                            min: 0,
                          })}
                          placeholder="0"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        * 부가세 포함 가격을 입력해주세요.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}
            {showImage && (
              <section className="space-y-4">
                <div className="flex items-center gap-2 text-lg font-semibold text-foreground/80">
                  <div className="p-1.5 bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 rounded-md">
                    <Image className="w-5 h-5" />
                  </div>
                  <h3>상품 이미지</h3>
                </div>
                <Card className="border-none shadow-sm h-full">
                  <CardContent className="p-6 space-y-4">
                    <Tabs
                      value={imageTab}
                      onValueChange={(v) => {
                        if (v === "upload" || v === "url") {
                          setImageTab(v);
                        }
                      }}
                      className="w-full"
                    >
                      <TabsList className="grid w-full grid-cols-2 h-9">
                        <TabsTrigger value="upload" className="text-xs">
                          파일 업로드
                        </TabsTrigger>
                        <TabsTrigger value="url" className="text-xs">
                          URL 입력
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="upload" className="mt-2">
                        <div className="flex items-center gap-2">
                          <Input
                            id="image-upload"
                            type="file"
                            accept={PRODUCT_IMAGE_EXTENSIONS.map(
                              (ext) => `.${ext}`,
                            ).join(",")}
                            disabled={isUploadingImage}
                            onChange={(e) => void handleImageChange(e)}
                            className="cursor-pointer text-xs file:mr-4 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 disabled:opacity-50"
                          />
                        </div>
                        <p className="mt-1.5 text-2xs text-muted-foreground">
                          {PRODUCT_IMAGE_EXTENSIONS.join(", ").toUpperCase()} ·
                          최대{" "}
                          {Math.floor(PRODUCT_IMAGE_MAX_BYTES / 1024 / 1024)}MB
                        </p>
                      </TabsContent>
                      <TabsContent value="url" className="mt-2">
                        <Input
                          id="image-url"
                          placeholder="https://example.com/image.jpg"
                          className="text-sm"
                          {...register("imageUrl", {
                            onBlur: handleImageUrlBlur,
                          })}
                        />
                        <p className="mt-1.5 text-2xs text-muted-foreground">
                          상품 페이지가 아니라 이미지 파일 주소를 넣어주세요
                        </p>
                      </TabsContent>
                    </Tabs>
                    {imageError && (
                      <p
                        role="alert"
                        className="text-xs text-destructive leading-relaxed"
                      >
                        {imageError}
                      </p>
                    )}
                    <div className="relative aspect-video w-full rounded-md border-2 border-dashed border-muted-foreground/20 bg-muted/10 flex items-center justify-center overflow-hidden group hover:border-primary/50 transition-colors">
                      {isUploadingImage ? (
                        <div className="flex flex-col items-center text-muted-foreground text-xs gap-2">
                          <Loader2 className="h-6 w-6 animate-spin" />
                          <span>업로드 중...</span>
                        </div>
                      ) : imageUrl ? (
                        <>
                          <img
                            src={imageUrl}
                            alt="Preview"
                            className="h-full w-full object-contain bg-card"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                            onClick={clearImage}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <div className="flex flex-col items-center text-muted-foreground text-xs gap-2">
                          <div className="p-3 bg-background rounded-full shadow-sm">
                            <Image className="h-6 w-6 opacity-50" />
                          </div>
                          <span>이미지 미리보기</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}
          </div>
          <DialogFooter className="sticky bottom-0 p-6 bg-background border-t z-10 mt-8">
            {slots?.footer ?? (
              <div className="flex w-full justify-end gap-3">
                {onReject && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={onReject}
                    className="mr-auto"
                  >
                    {rejectLabel}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="px-6"
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || isUploadingImage}
                  className="px-6 bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploadingImage ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>이미지 업로드 중...</span>
                    </div>
                  ) : isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-background border-t-current rounded-full animate-spin" />
                      <span>처리 중...</span>
                    </div>
                  ) : (
                    submitLabel
                  )}
                </Button>
              </div>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ProductForm = React.forwardRef<HTMLDivElement, ProductFormProps>(
  (props, _ref) => <ProductFormComponent {...props} />,
);
ProductForm.displayName = "ProductForm";
